import {
  LOCALE_COOKIE_NAME,
  LOCALE_OVERRIDE_STORAGE_PREFIX,
  OFFERED_LOCALES,
  type AppLocale,
} from "@/i18n/routing";

/** The language when the browser asks for none we offer. */
export const BROWSER_FALLBACK_LOCALE: AppLocale = "en";

export function normalizeAppLocale(value: string | null | undefined): AppLocale | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith("fr")) return "fr";
  if (normalized.startsWith("en")) return "en";
  if (normalized.startsWith("rw")) return "rw";
  return null;
}

/**
 * The browser's language when it is one the app offers, else English. Walks the browser's whole preference
 * list (a French speaker with an English OS still gets French), and never lands on a paused language: a
 * Kinyarwanda browser reads English, not a half-translated screen.
 */
export function getBrowserLocale(): AppLocale {
  if (typeof navigator === "undefined") return BROWSER_FALLBACK_LOCALE;
  const preferences = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const preference of preferences) {
    const locale = normalizeAppLocale(preference);
    if (locale && OFFERED_LOCALES.includes(locale)) return locale;
  }
  return BROWSER_FALLBACK_LOCALE;
}

/**
 * An explicit `?lang=` (or `?language=`) query param — the marketing site forwards the visitor's
 * chosen locale this way when it deep-links into the tenant app (login, create-church, …). Honored
 * pre-auth as the highest-priority signal so the entry screen opens in the same language.
 */
export function getUrlLocale(): AppLocale | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return normalizeAppLocale(params.get("lang") ?? params.get("language"));
}

export function getLocaleCookie(): AppLocale | null {
  if (typeof document === "undefined") return null;
  const cookieMatch = document.cookie.match(new RegExp(`${LOCALE_COOKIE_NAME}=([^;]+)`));
  return normalizeAppLocale(cookieMatch?.[1]);
}

export function getLocaleOverrideStorageKey(workspaceSlug: string | null | undefined) {
  return `${LOCALE_OVERRIDE_STORAGE_PREFIX}${workspaceSlug?.trim() || "global"}`;
}

export function getPersistedLocaleOverride(workspaceSlug: string | null | undefined): AppLocale | null {
  if (typeof window === "undefined") return null;
  return normalizeAppLocale(window.localStorage.getItem(getLocaleOverrideStorageKey(workspaceSlug)));
}

export function persistLocaleOverride(input: {
  locale: AppLocale;
  workspaceSlug?: string | null;
  rememberAsOverride: boolean;
}) {
  if (typeof window === "undefined") return;

  document.cookie = `${LOCALE_COOKIE_NAME}=${input.locale}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;

  if (input.rememberAsOverride) {
    window.localStorage.setItem(getLocaleOverrideStorageKey(input.workspaceSlug), input.locale);
  }
}

export function resolveRuntimeLocale(input: {
  authMeLoaded: boolean;
  activeWorkspaceDefaultLanguage?: string | null;
  activeWorkspaceSlug?: string | null;
}): AppLocale {
  const workspaceOverride = getPersistedLocaleOverride(input.activeWorkspaceSlug);
  if (workspaceOverride) return workspaceOverride;

  if (input.authMeLoaded) {
    const workspaceDefault = normalizeAppLocale(input.activeWorkspaceDefaultLanguage);
    if (workspaceDefault) return workspaceDefault;

    const globalOverride = getPersistedLocaleOverride(null);
    if (globalOverride) return globalOverride;

    // No choice anywhere: the browser's language, else English.
    return getBrowserLocale();
  }

  return getUrlLocale() ?? getLocaleCookie() ?? getBrowserLocale();
}
